import React from 'react';
import { PageSection, SiteData, BlogPost } from '../../../types';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  onSelectPost?: (post: BlogPost) => void;
}

export const BlogPreviewSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  onSelectPost,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'GÜNCEL YAZILAR';
  const title = content.title || 'Haberler & Sektörel Analizler';
  const description =
    content.description ||
    'Sektördeki en son trendler, uzman görüşleri ve güncel gelişmelerden haberdar olun.';

  const defaultPosts: BlogPost[] = [
    {
      id: 'blog-1',
      siteId: siteData.id,
      title: '2026 Küresel İş Dünyasında Stratejik Yönetim Trendleri',
      slug: '2026-stratejik-yonetim-trendleri',
      summary: 'Yapay zeka entegrasyonu, sürdürülebilir kurumsal yapılar ve veri temelli karar alma modelleri.',
      content: '',
      authorName: 'Dr. Selim Aras',
      coverImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800&auto=format&fit=crop',
      publishedAt: Date.now(),
      category: 'Yönetim',
      tags: ['Strateji', 'Yönetim'],
      status: 'PUBLISHED',
      readTimeMinutes: 5,
    },
    {
      id: 'blog-2',
      siteId: siteData.id,
      title: 'Ege ve Akdeniz’de Mavi Yolculuk Rehberi: En Sakin Koylar',
      slug: 'mavi-yolculuk-en-sakin-koylar',
      summary: 'Göcek, Bozburun ve Datça koylarında kalabalıktan uzak, huzurlu bir charter rotası planlama kılavuzu.',
      content: '',
      authorName: 'Kaptan Deniz',
      coverImage: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?q=80&w=800&auto=format&fit=crop',
      publishedAt: Date.now() - 86400000 * 3,
      category: 'Mavi Tur',
      tags: ['Charter', 'Rota'],
      status: 'PUBLISHED',
      readTimeMinutes: 4,
    },
  ];

  const bgColor = style.backgroundColor || '#FFFFFF';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-white/10' : 'bg-black/5'
              }`}
              style={{ color: accentColor }}
            >
              {badge}
            </div>
          )}
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm sm:text-base leading-relaxed max-w-xl mx-auto" style={{ color: mutedTextColor }}>{description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {defaultPosts.slice(0, 3).map((post) => (
            <div
              key={post.id}
              onClick={() => {
                if (onSelectPost) onSelectPost(post);
                else if (onNavigate) onNavigate('/blog');
              }}
              className={`rounded-3xl overflow-hidden border shadow-xs hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer flex flex-col justify-between group ${
                isDark
                  ? 'bg-slate-900 border-white/10 text-white'
                  : 'bg-white border-slate-200/80 text-slate-900'
              }`}
            >
              <div className="aspect-16/9 overflow-hidden bg-slate-900 relative">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {post.category && (
                  <span className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                )}
              </div>

              <div className="p-6 space-y-3 flex-1 flex flex-col justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(post.publishedAt).toLocaleDateString('tr-TR')}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {post.authorName}
                    </span>
                  </div>
                  <h3
                    className={`font-extrabold text-base transition leading-snug ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    {post.title}
                  </h3>
                  <p className={`text-xs sm:text-sm line-clamp-2 leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {post.summary}
                  </p>
                </div>

                <div
                  className="pt-3 flex items-center text-xs font-bold group-hover:translate-x-1 transition gap-1"
                  style={{ color: accentColor }}
                >
                  <span>Devamını Oku</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
